import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Eye } from 'lucide-react';
import { CATEGORIES } from '@/data';
import type { Clip } from '@/types';

interface DraggableClipCardProps {
  clip: Clip;
  disabled?: boolean;
  onPreview?: (clip: Clip) => void;
}

export const DraggableClipCard: React.FC<DraggableClipCardProps> = ({
  clip,
  disabled = false,
  onPreview,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: clip.id,
    data: {
      type: 'clip',
      clip,
    },
    disabled,
  });

  const category = CATEGORIES.find(c => c.id === clip.categoryId);

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 50 : undefined,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        bg-zinc-700/50 hover:bg-zinc-700 rounded-lg p-2
        border border-zinc-600
        transition-colors flex items-center gap-2
        touch-none
        ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-lime-500/50 cursor-grabbing' : 'cursor-grab'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      {/* Drag handle */}
      <div className="p-1 rounded hover:bg-zinc-600">
        <GripVertical className="w-4 h-4 text-zinc-500" />
      </div>

      {/* Category emoji */}
      <span className="text-lg flex-shrink-0">{category?.emoji || '📎'}</span>

      {/* Clip info - clickable for preview */}
      <div
        className="flex-1 min-w-0 cursor-pointer hover:text-lime-400"
        onClick={() => onPreview?.(clip)}
      >
        <p className="text-white text-sm font-medium truncate">{clip.title}</p>
        <p className="text-zinc-500 text-xs truncate">{category?.name || 'Unknown'}</p>
      </div>

      {/* Preview button */}
      {onPreview && (
        <button
          onClick={() => onPreview(clip)}
          className="p-1.5 rounded-lg text-zinc-500 hover:text-lime-400 hover:bg-zinc-600 transition-colors"
          title="Preview clip"
        >
          <Eye className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
